/* eslint-disable react/no-unescaped-entities */
import React from 'react'
import { Button, ButtonText } from '@/components/ui/button'
import { Link, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { useForm, useWatch } from 'react-hook-form'
import Field from '@/components/ui/Field'
import { Label } from '@/components/ui/Label'
import { PasswordInput } from '@/components/PasswordInput'
import { Input } from '@/components/ui/Input'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import useResetPassword from '@/services/api/auth/useResetPassword'
import NewPasswordSuccess from './NewPasswordSuccess'
import useCheckResetPasswordLink from '@/services/api/auth/useCheckResetPasswordLink'

function ResetLinkExpired() {
  return (
    <div className="pf-auth grid min-h-screen">
      <Card className="place-self-center">
        <CardHeader>
          <CardTitle>Reset link expired</CardTitle>
        </CardHeader>
        <CardContent className="text-txt-secondary">
          <p>
            This password reset link has expired or has already been used. If
            you asked for more than one reset email, only the link in the most
            recent one works.
          </p>
          <Link to="/forgot-password" className="mt-4 block w-full">
            <Button size="lg" className="w-full">
              <ButtonText>Request a new link</ButtonText>
            </Button>
          </Link>
          <p className="mt-4 text-center text-base">
            <Link
              to="/login"
              className="text-center text-txt-secondary no-underline"
            >
              Return to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

const schema = yup.object().shape({
  email: yup.string().email().required().label('Email'),
  password: yup.string().required().label('Password'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match'),
})

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const linkEmail = searchParams.get('email')
  const token = searchParams.get('token')

  const { register, control, handleSubmit, formState, setError } = useForm({
    defaultValues: {
      email: linkEmail ?? '',
    },
    resolver: yupResolver(schema),
  })

  const { status: linkStatus } = useCheckResetPasswordLink({
    email: linkEmail,
    token,
  })

  const { action: resetPassword, status } = useResetPassword({ setError })

  const { errors } = formState

  async function handleForm(val) {
    resetPassword({
      email: val?.email,
      password: val?.password,
      token,
    })
  }

  if (!token || linkStatus === 'error') return <ResetLinkExpired />

  if (linkEmail && linkStatus === 'pending')
    return (
      <div className="pf-auth grid min-h-screen">
        <p className="place-self-center text-txt-secondary">Checking link…</p>
      </div>
    )

  if (status.status === 'success')
    return (
      <div className="pf-auth grid min-h-screen">
        <div className="place-self-center">
          <NewPasswordSuccess />
        </div>
      </div>
    )

  return (
    <div className="pf-auth grid min-h-screen">
      <Card className="place-self-center">
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Enter a new password for your account below.</p>
          <form className="mt-4 grid gap-3" onSubmit={handleSubmit(handleForm)}>
            <div>
              <Field
                large
                status={errors.email ? 'error' : null}
                statusText={errors.email?.message}
              >
                <Label>Email</Label>
                <Input placeholder="example@gmail.com" {...register('email')} />
                <FieldStatusMessage />
              </Field>
            </div>
            <div>
              <Field
                large
                status={errors.password ? 'error' : null}
                statusText={errors.password?.message}
              >
                <Label>New Password</Label>
                <PasswordInput
                  placeholder="Enter your new password"
                  {...register('password')}
                />
                <FieldStatusMessage />
              </Field>
            </div>

            <div>
              <Field
                large
                status={errors.confirmPassword ? 'error' : null}
                statusText={errors.confirmPassword?.message}
              >
                <Label>Confirm New Password</Label>
                <PasswordInput
                  placeholder="Re-enter your new password"
                  {...register('confirmPassword')}
                />
                <FieldStatusMessage />
              </Field>
            </div>

            <button className="hidden" type="submit">
              submit
            </button>
          </form>

          <div className="mt-7">
            <Button
              type="submit"
              onClick={handleSubmit(handleForm)}
              loading={status.status === 'pending'}
              size="lg"
              className="w-full"
            >
              <ButtonText>
                {status.status === 'pending' ? 'Please wait' : 'Save Password'}
              </ButtonText>
            </Button>
            <p className="mt-4 text-center text-base text-txt-secondary">
              <Link
                to="/login"
                className="text-center text-txt-secondary no-underline"
              >
                Return to sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
